import { useState } from 'react';
import { useNavigate } from 'react-router';
import { HOME, LEGAL_AID_FORM, legalAidHeading, statParts } from '@/data/home';
import { Reveal } from '@components/motion';
import FigmaFrame from './FigmaFrame';
import LegalAidForm from './LegalAidForm';
import { Img } from '@/components/Img';

const JUSTICE_ART = '/landing/legal-aid/gemini.webp';
const CARD_TALL_PHOTO = '/landing/legal-aid/card-tall.webp';
const CARD_WIDE_PHOTO = '/landing/legal-aid/card-wide.webp';

const AVATARS = [
    { src: '/landing/legal-aid/avatar-new-1.webp', bg: '#c9cbb4' },
    { src: '/landing/legal-aid/avatar-new-2.webp', bg: '#ffd88d' },
    { src: '/landing/legal-aid/avatar-new-3.webp', bg: '#cabdff' },
    { src: '/landing/legal-aid/avatar-new-4.webp', bg: '#f5f5f5' },
];

function LegalAidDesktop({ onOpen }: { onOpen: () => void }) {
    const heading = legalAidHeading(HOME.legalAid.heading);
    const stat = statParts(HOME.legalAid.stat.value);
    const { wideCard: wide, tallCard: tall } = HOME.legalAid;
    return (
        <FigmaFrame designWidth={1920} designHeight={812} className="w-full">
            <div className="relative h-[812px] w-[1920px] bg-[#1b1b1b] text-white">
                {/* Tall photo card — "Cases resolved" testimonial (event) */}
                <Reveal
                    as="div"
                    once
                    variant="fade-left"
                    delay={120}
                    className="absolute left-[1520px] top-[114px] h-[602px] w-[355px] overflow-hidden rounded-[30px] bg-[#f5f5f5]"
                >
                    <Img
                        src={tall.image || CARD_TALL_PHOTO}
                        alt={tall.image ? tall.imageAlt : ''}
                        className="absolute inset-0 size-full object-cover"
                    />
                    <p className="absolute left-[calc(50%-163.5px)] top-[448px] w-[302px] break-words text-[28px] font-bold leading-[29px] tracking-[-0.5px] text-white">
                        {tall.title}
                    </p>
                    <p className="absolute left-[calc(50%-163.5px)] top-[545px] w-[337px] -translate-y-1/2 text-[16px] font-normal leading-[24px] text-white">
                        {tall.body}
                    </p>
                </Reveal>

                {/* 33k+ stat */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={220}
                    className="absolute left-[1086px] top-[482px] flex w-[311.156px] flex-col gap-[6px] whitespace-nowrap"
                >
                    <p className="text-[84px] font-bold leading-none tracking-[-2.52px] text-white">
                        {stat.figure}
                        {stat.plus && <span className="text-[#f37b23]">{stat.plus}</span>}
                    </p>
                    <p className="text-[19px] font-medium leading-[1.7] text-[#9a9a9a]">{HOME.legalAid.stat.label}</p>
                </Reveal>

                {/* Wide photo card — interview */}
                <Reveal
                    as="div"
                    once
                    variant="fade-left"
                    className="absolute left-[1086px] top-[114px] h-[330px] w-[392px] overflow-hidden rounded-[30px] bg-[#f5f5f5]"
                >
                    <Img
                        src={wide.image || CARD_WIDE_PHOTO}
                        alt={wide.image ? wide.imageAlt : ''}
                        className="absolute inset-0 size-full object-cover"
                    />
                    <p className="absolute left-[calc(50%-169px)] top-[178px] w-[230px] break-words text-[28px] font-bold leading-[29px] tracking-[-0.5px] text-white">
                        {wide.title}
                    </p>
                    <p className="absolute left-[calc(50%-169px)] top-[275px] w-[333px] -translate-y-1/2 text-[16px] font-normal leading-[24px] text-white">
                        {wide.body}
                    </p>
                </Reveal>

                {/* Decorative Lady Justice line art */}
                <div className="absolute left-[625px] top-[181px] h-[639px] w-[518px] overflow-hidden opacity-[0.06]">
                    <Img
                        src={JUSTICE_ART}
                        alt=""
                        className="absolute left-0 top-[0.07%] h-[169.5%] w-full max-w-none"
                    />
                </div>

                {/* Heading + paragraph + CTA */}
                <Reveal
                    as="div"
                    once
                    variant="fade-right"
                    className="absolute left-[134px] top-1/2 h-[424px] w-[662.459px] -translate-y-1/2"
                >
                    <div className="absolute left-0 top-[103px] flex -translate-y-1/2 flex-col justify-center text-[96px] font-extrabold text-white">
                        {heading.lead && (
                            <p className="mb-0 font-normal leading-[103px] text-[#6c6c6c]">{heading.lead}&nbsp;</p>
                        )}
                        <p className="whitespace-pre">
                            <span className="leading-[103px]">{heading.bold}</span>
                            {heading.mark && <span className="leading-[103px] text-[#f60]">{heading.mark}</span>}
                        </p>
                    </div>
                    <p className="absolute left-0 top-[251px] w-[662.459px] break-words text-[18px] font-normal leading-[28px] text-white">
                        {HOME.legalAid.paragraph}
                    </p>
                    <button
                        type="button"
                        onClick={onOpen}
                        className="absolute left-[9px] top-[380px] flex w-[216px] cursor-pointer items-center justify-center rounded-[10px] bg-[#f60] px-[20px] py-[10px] text-[16px] font-bold leading-[24px] text-white transition-transform duration-300 hover:scale-[1.03]"
                    >
                        {HOME.legalAid.ctaLabel}
                    </button>
                </Reveal>

                {/* Avatar row */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={320}
                    className="absolute left-[1086px] top-[623px] flex h-[71.466px] w-[457.13px] items-center gap-[7px]"
                >
                    {AVATARS.map((a) => (
                        <div
                            key={a.src}
                            className="size-[70px] shrink-0 overflow-hidden rounded-[57.6px]"
                            style={{ backgroundColor: a.bg }}
                        >
                            <Img src={a.src} alt="" className="size-full object-cover" />
                        </div>
                    ))}
                    <p className="text-[74px] font-bold leading-none tracking-[-2.22px] text-white">+</p>
                </Reveal>
            </div>
        </FigmaFrame>
    );
}

function LegalAidMobile({ onOpen }: { onOpen: () => void }) {
    const heading = legalAidHeading(HOME.legalAid.heading);
    return (
        <section className="bg-[#1b1b1b] px-5 pb-[88px] pt-[129px] text-white">
            <h2 className="flex flex-col text-[40px] font-extrabold">
                {heading.lead && <span className="font-normal leading-[56px] text-[#6c6c6c]">{heading.lead}</span>}
                <span className="leading-[56px]">
                    {heading.bold}
                    {heading.mark && <span className="text-[#f60]">{heading.mark}</span>}
                </span>
            </h2>

            <p className="mt-[22px] text-[14px] font-normal leading-[24px] text-white">{HOME.legalAid.paragraph}</p>

            <button
                type="button"
                onClick={onOpen}
                className="mt-[34px] flex w-[216px] items-center justify-center rounded-[10px] bg-[#f60] px-5 py-2.5 text-[16px] font-bold leading-[24px] text-white"
            >
                {HOME.legalAid.ctaLabel}
            </button>
        </section>
    );
}

export default function LegalAidCTA() {
    // One panel serves both breakpoints, so the open state lives here.
    const [formOpen, setFormOpen] = useState(false);
    const navigate = useNavigate();
    // The CMS decides where the button goes: the request form on this page, or a page of its own.
    const open = () => {
        const href = HOME.legalAid.ctaHref.trim();
        if (!href || href === LEGAL_AID_FORM) setFormOpen(true);
        else if (/^https?:\/\//.test(href)) window.open(href, '_blank', 'noopener');
        else void navigate(href);
    };

    return (
        <>
            <div className="hidden lg:block">
                <LegalAidDesktop onOpen={open} />
            </div>
            <div className="lg:hidden">
                <LegalAidMobile onOpen={open} />
            </div>

            <LegalAidForm open={formOpen} onClose={() => setFormOpen(false)} />
        </>
    );
}
